import { useEffect, useState, type FormEvent } from 'react';
import { ageFrom, BANNED_CONTENT, MIN_AGE, MIN_BROADCAST_AGE, TERMS_VERSION, type AgeGroup } from '@zip2live/shared/safety';
import { ageGroupFor, agree, currentAgreement, finishRules, markTooYoung, tooYoung, useRulesRequest } from '../lib/rules.ts';
import { Sheet } from './Safety.tsx';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

type Step = 'loading' | 'age' | 'rules' | 'too-young' | 'adults-only';

/**
 * The rules dialog (lib/rules.ts), mounted once for the whole site: when you were born (month and
 * year, of which only the age group is kept), then ZIP2LIVE's zero-tolerance rules and "I agree".
 */
export function RulesDialog() {
  const req = useRulesRequest();
  if (!req) return null;
  return <Dialog key={req.id} purpose={req.purpose} />;
}

function Dialog({ purpose }: { purpose: 'live' | 'chat' }) {
  const [step, setStep] = useState<Step>('loading');
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void currentAgreement()
      .catch(() => null)
      .then((a) => {
        if (tooYoung()) return setStep('too-young');
        if (a && a.version === TERMS_VERSION && purpose === 'live' && a.ageGroup !== 'adult') return setStep('adults-only');
        // Agreed to older rules: the age they gave then still stands.
        if (a) setAgeGroup(a.ageGroup);
        setStep(a ? 'rules' : 'age');
      });
  }, [purpose]);

  const checkAge = (e: FormEvent) => {
    e.preventDefault();
    const y = Number(year);
    const thisYear = new Date().getFullYear();
    if (!month || !/^\d{4}$/.test(year) || y < thisYear - 120 || y > thisYear) return setError('Enter the month and year you were born.');
    const group = ageGroupFor(ageFrom(y, Number(month)));
    if (!group) {
      markTooYoung();
      return setStep('too-young');
    }
    setAgeGroup(group);
    setError(null);
    setStep('rules');
  };

  const accept = async () => {
    if (!ageGroup) return setStep('age');
    setBusy(true);
    setError(null);
    try {
      await agree(ageGroup);
      if (purpose === 'live' && ageGroup !== 'adult') return setStep('adults-only');
      finishRules(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => finishRules(false);
  const heading = purpose === 'live' ? 'Before you go live' : 'Before you chat';
  const field = 'mt-1 w-full rounded-xl border-2 border-line bg-white px-3.5 py-2.5 text-[16px] outline-none focus:border-navy';

  if (step === 'loading') return null;
  if (step === 'too-young') {
    return (
      <Sheet title="Sorry, not yet" onClose={close}>
        <p className="mt-2 text-[15px] text-muted">You need to be {MIN_AGE} or older to use ZIP2LIVE.</p>
        <button type="button" className="btn-race mt-5 h-12 w-full text-lg" onClick={close}>OK</button>
      </Sheet>
    );
  }
  if (step === 'adults-only') {
    return (
      <Sheet title={`Going live is ${MIN_BROADCAST_AGE}+`} onClose={close}>
        <p className="mt-2 text-[15px] text-muted">You need to be {MIN_BROADCAST_AGE} or older to go live on ZIP2LIVE. You can still watch streams and chat.</p>
        <button type="button" className="btn-race mt-5 h-12 w-full text-lg" onClick={close}>OK</button>
      </Sheet>
    );
  }
  if (step === 'age') {
    return (
      <Sheet title="When were you born?" onClose={close}>
        <p className="mt-1 text-xs font-extrabold tracking-[0.14em] text-muted uppercase">{heading}</p>
        <p className="mt-2 text-[15px] text-muted">We ask everyone once. We keep only your age group, never your birth date.</p>
        <form onSubmit={checkAge}>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <label className="mt-2 block text-sm font-bold">
              Month
              <select value={month} onChange={(e) => { setMonth(e.target.value); setError(null); }} className={field} required>
                <option value="" disabled>Month</option>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="mt-2 block text-sm font-bold">
              Year
              <input
                value={year}
                onChange={(e) => { setYear(e.target.value.replace(/\D/g, '').slice(0, 4)); setError(null); }}
                inputMode="numeric"
                autoComplete="off"
                placeholder="YYYY"
                className={field}
                required
              />
            </label>
          </div>
          {error && <p role="alert" className="mt-3 font-bold text-race">{error}</p>}
          <button type="submit" disabled={!month || year.length !== 4} className="btn-race mt-5 h-12 w-full text-lg disabled:opacity-50">CONTINUE</button>
          <button type="button" className="mt-3 h-11 w-full font-bold text-muted underline underline-offset-2" onClick={close}>Not now</button>
        </form>
      </Sheet>
    );
  }
  return (
    <Sheet title="ZIP2LIVE’s rules" onClose={close}>
      <p className="mt-1 text-xs font-extrabold tracking-[0.14em] text-muted uppercase">{heading}</p>
      <p className="mt-2 text-[15px] text-muted">
        ZIP2LIVE has <b className="text-navy">zero tolerance</b> for objectionable content and abusive users. Never stream, post or say:
      </p>
      <ul className="mt-3 space-y-1.5 rounded-2xl bg-mist p-4 text-[15px] font-semibold">
        {BANNED_CONTENT.map((rule) => (
          <li key={rule} className="flex gap-2.5">
            <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-race" aria-hidden />
            {rule}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-muted">Every stream is checked automatically, and we review reports within 24 hours. Break the rules and your stream comes down and your account is suspended.</p>
      <p className="mt-2 text-sm">
        <a href="/terms" target="_blank" rel="noopener" className="font-bold text-navy underline underline-offset-2">Read the full terms</a>
        <span className="mx-2 text-muted">·</span>
        <a href="/privacy" target="_blank" rel="noopener" className="font-bold text-navy underline underline-offset-2">Privacy policy</a>
      </p>
      {error && <p role="alert" className="mt-3 font-bold text-race">{error}</p>}
      <button type="button" disabled={busy} className="btn-race mt-5 h-12 w-full text-lg" onClick={() => void accept()}>
        {busy ? 'SAVING…' : 'I AGREE'}
      </button>
      <button type="button" className="mt-3 h-11 w-full font-bold text-muted underline underline-offset-2" onClick={close}>Not now</button>
    </Sheet>
  );
}
